// ─── Orden libre de las filas de la timeline ────────────────────────────────
// Las filas (capas) de la timeline se dibujan en el orden que el usuario
// quiera: un texto puede quedar ENTRE dos videos, el audio arriba de todo, etc.
// Ese orden es una lista de claves de fila guardada con el proyecto; acá viven
// las dos operaciones puras que la manejan.
//
// Por qué una lista de claves y no un número por fila: las filas no son
// entidades guardadas, se derivan de los clips (familia + `trackIndex`), así
// que aparecen y desaparecen solas. La lista tolera eso — se reconcilia contra
// las filas que existen hoy— sin obligar a renumerar nada.
// Ver ADR-2026-09-15-el-orden-de-la-timeline-manda-en-el-preview.

/** Acomoda las filas de hoy según el orden guardado.
 *
 *  Las claves guardadas que ya no existen se ignoran. Las filas nuevas —una
 *  capa recién creada, la fila de subtítulos que aparece— se meten JUSTO
 *  DESPUÉS de su vecina natural de arriba que ya esté colocada, así entran
 *  donde el usuario las esperaría y no todas juntas al final. */
export function ordenarFilas<T extends { key: string }>(
  naturales: T[],
  guardado: string[],
): T[] {
  const porClave = new Map(naturales.map((f) => [f.key, f]));
  const resultado: string[] = [];
  const puestas = new Set<string>();
  for (const clave of guardado) {
    if (!porClave.has(clave) || puestas.has(clave)) continue;
    resultado.push(clave);
    puestas.add(clave);
  }
  naturales.forEach((fila, i) => {
    if (puestas.has(fila.key)) return;
    // La primera vecina de arriba (en el orden natural) que ya esté colocada.
    let anclaje = -1;
    for (let j = i - 1; j >= 0; j--) {
      const pos = resultado.indexOf(naturales[j].key);
      if (pos >= 0) {
        anclaje = pos;
        break;
      }
    }
    resultado.splice(anclaje + 1, 0, fila.key);
    puestas.add(fila.key);
  });
  return resultado.map((k) => porClave.get(k)!);
}

/** Mueve una clave al hueco `posicion`.
 *
 *  `posicion` es un HUECO contado sobre la lista COMPLETA (0 = arriba de todo,
 *  n = abajo de todo), que es como lo mide el arrastre: el usuario apunta a un
 *  espacio entre filas y no sabe nada de que la fila que arrastra va a salir de
 *  su lugar. Acá se descuenta esa salida. */
export function moverFila(
  orden: string[],
  clave: string,
  posicion: number,
): string[] {
  const desde = orden.indexOf(clave);
  const sinElla = orden.filter((k) => k !== clave);
  const pedido = desde >= 0 && posicion > desde ? posicion - 1 : posicion;
  const pos = Math.max(0, Math.min(sinElla.length, pedido));
  return [...sinElla.slice(0, pos), clave, ...sinElla.slice(pos)];
}

/** Inserta una clave NUEVA en el hueco `posicion` (sin descontar nada: la fila
 *  no estaba antes). Si ya estaba, equivale a moverla. */
export function insertarFila(
  orden: string[],
  clave: string,
  posicion: number,
): string[] {
  if (orden.includes(clave)) return moverFila(orden, clave, posicion);
  const pos = Math.max(0, Math.min(orden.length, posicion));
  return [...orden.slice(0, pos), clave, ...orden.slice(pos)];
}
